/*
 * File: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_backoffice\src\Content\Offers\OfferRowUserSingle\OfferRowUser.tsx
 * Project: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_backoffice
 * Created Date: Wednesday October 9th 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Wednesday October 9th 2024 3:43:03 pm
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { useEffect, useState } from "react";
import { Button, TableCell } from "@mui/material";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import translator from "MultiLanguage/TranslationWrapper";
import { OfferStatus, OfferUserType, UpdatableSingleOffer } from "Type/offer.type";
import { Link } from "react-router-dom";
import Styles from "Content/Offers/OfferRowUserSingle/OfferRowUser.module.css";
import OfferService from "Services/Offer/OfferService";
import { FaDotCircle } from "react-icons/fa";
import { offerColor } from "Utils/offer.utils";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import LoadingScreen from "Content/LoadingScreen/LoadingScreen";
import CollapsRow from "../CollapsRow/CollapsRow";
import { OfferCollapsContent } from "../OfferCollapsContent/OfferCollapsContent";

type OfferRowUserProps = {
    Offer: OfferUserType;
    showUser?: boolean
};

export function OfferRowUser(props: OfferRowUserProps) {
    const [price, setPrice] = useState(props.Offer.Price);
    const [quantity, setQuantity] = useState(props.Offer.Quantity);
    const [mileage, setMileage] = useState(props.Offer.Mileage);
    const [month, setMonth] = useState(props.Offer.Month);
    const [status, setStatus] = useState(props.Offer.Status);
    const [comments, setComments] = useState(props.Offer.Comments);
    const [fileCount, setFilecount] = useState(props.Offer._count.OfferFile);
    const [isSendable, setIsSendable] = useState(false);
    const statusOptions = ["ACCEPTED", "ARCHIVED", "CANCEL"];
    const [spinner, setSpinner] = useState(false);
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [additionnalText, setAdditionnalText] = useState<string | string[] | undefined>();

    useEffect(() => {
        const isDifferent =
            price !== props.Offer.Price ||
            mileage !== props.Offer.Mileage ||
            month !== props.Offer.Month ||
            status !== props.Offer.Status ||
            comments !== props.Offer.Comments;
        setIsSendable(isDifferent);
    }, [price, mileage, month, status, comments]);

    const handleResponse = (text: string | string[], isOkay: boolean = false) => {
        setIsOkay(isOkay);
        setOpenModal(true);
        setSpinner(false);
        setAdditionnalText(text);
    };

    const HandleCarCountChange = (nbCar: number, isDelete: boolean = false) => {
        if (!isDelete)
            setQuantity(quantity + nbCar);
        else
            setQuantity(quantity - nbCar);
    };

    const HandleFileCountChange = (nbFile: number, isDelete: boolean = false) => {
        if (!isDelete)
            setFilecount(fileCount + nbFile);
        else
            setFilecount(fileCount - nbFile);
    };

    const handleSave = () => {
        const data: UpdatableSingleOffer = {};
        if (price !== props.Offer.Price)
            data.Price = price;
        if (status !== props.Offer.Status)
            data.Status = status;
        if (comments !== props.Offer.Comments)
            data.Comments = comments;
        if (mileage !== props.Offer.Mileage)
            data.Mileage = mileage;
        if (month !== props.Offer.Month)
            data.Month = month;
        setSpinner(true);
        setIsSendable(false);
        OfferService.updateSingleOffer(props.Offer.id, data).then(() => {
            handleResponse("", true);
            setIsSendable(false);
        }).catch((error)=> {
            console.log(error);
            handleResponse(["Une erreur est survenue:", error.message]);
        });
    };

    return (
        <>
            <CollapsRow collapseChildren={<OfferCollapsContent offerID={props.Offer.id} setterFileCount={HandleFileCountChange} setterCarCount={HandleCarCountChange} />}>
                <>
                    {
                        props.showUser ?
                        <TableCell align="center" className={`${Styles.cells} text-capitalize`}>
                            <Link to={`${RouterPathResolver.pathUser_BY_ID.path.replace(":id", props.Offer.User.id)}`}>
                                {`${props.Offer.User.FirstName} ${props.Offer.User.LastName}`}
                            </Link>
                        </TableCell>
                        : null
                    }
                </>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{new Date(props.Offer.CreatedAt).toLocaleString()}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{new Date(props.Offer.UpdatedAt).toLocaleString()}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <Link to={RouterPathResolver.pathCar_BY_ID.path.replace(":id", props.Offer.Car.id)} className="m-0 text-capitalize">{props.Offer.Car.Brand} {props.Offer.Car.Model}</Link>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <p className="m-0">{quantity}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <input className={`${Styles.inputNumber}`} type="number" value={price} min={1} onChange={(e) => setPrice(parseInt(e.target.value))} />
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <input className={`${Styles.inputNumber}`} type="number" value={mileage} min={1} onChange={(e) => setMileage(parseInt(e.target.value))} />
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <input className={`${Styles.inputNumber}`} type="number" value={month} min={1} onChange={(e) => setMonth(parseInt(e.target.value))} />
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`} >
                    <p className="m-0">{fileCount}</p>
                </TableCell>
                <TableCell align="center" className={`${Styles.status}`}>
                    <FaDotCircle className="me-2" fill={`${offerColor(status as unknown as string)}`}/>
                    <select className={`${Styles.statusSelector}`} name="status" value={status} onChange={(e) => setStatus(e.target.value as unknown as OfferStatus)}>
                        <option disabled value="PENDING">{translator.decode("Status.PENDING")}</option>
                        {statusOptions.map(val => <option key={`${val}_${Math.random()}`} value={val}>{translator.decode(`Status.${val}`)}</option>)}
                    </select>
                </TableCell>
                <TableCell align="center">
                    <textarea maxLength={2048} className={`${Styles.comments}`} value={comments} onChange={(e) => setComments(e.target.value)} />
                </TableCell>
                <TableCell align="center" className={`${Styles.cells}`}>
                    <Button disabled={!isSendable} onClick={handleSave} >{translator.decode("dynamic.save")}</Button>
                    {
                        spinner ?
                            <LoadingScreen width="2rem" height="2rem" borderWidth="5px" />
                        :null
                    }
                </TableCell>
            </CollapsRow>
            <ModalConfirmation show={openModal} onClose={() => setOpenModal(false)} closeModal={() => setOpenModal(false)} isOkay={isOkay} additionnalMessage={additionnalText}/>
        </>
    )
};
